import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { payslips as api, getSettings, apiError, logoUrl, payslipPdfUrl } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Loading } from "@/components/Shared";
import { WiseAceLogo } from "@/components/WiseAceLogo";
import { ShareButton } from "@/components/ShareButton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";

export default function PayslipView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [s, setS] = useState(null);

  useEffect(() => { Promise.all([api.get(id), getSettings()]).then(([d, st]) => { setDoc(d); setS(st); }).catch((e) => toast.error(apiError(e))); }, [id]);
  if (!doc) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 no-print">
        <button onClick={() => nav("/payslips")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B132B]" data-testid="payslip-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        <a href={payslipPdfUrl(id)} download data-testid="print-payslip"><Button variant="outline"><Printer className="w-4 h-4 mr-1.5" /> Download PDF</Button></a>
        <ShareButton docType="payslip" docId={id} />
      </div>

      <div className="print-area max-w-[794px] mx-auto bg-white p-8 md:p-12 shadow-2xl rounded-sm border border-slate-200 my-6" data-testid="payslip-document">
        <div className="flex justify-between items-start pb-6 border-b-2 border-[#0B132B]">
          <div>
            {s?.logo_path ? <img src={logoUrl()} alt="Business logo" className="h-14 max-w-[220px] object-contain mb-2" /> : s?.logo_url ? <img src={s.logo_url} alt="Business logo" className="h-14 max-w-[220px] object-contain mb-2" /> : <WiseAceLogo size={44} />}
            <div className="mt-3 text-sm">
              <div className="font-semibold text-[#0B132B]">{s?.company_name}</div>
              {s?.address && <div className="text-slate-500">{s.address}</div>}
              {s?.vat_number && <div className="text-slate-500">VAT: {s.vat_number}</div>}
            </div>
          </div>
          <div className="text-right">
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0B132B]">PAYSLIP</h1>
            <div className="text-[#D4AF37] font-mono font-semibold mt-1">{doc.number}</div>
            <div className="text-xs text-slate-500 mt-2">Period: {doc.period}</div>
            <div className="text-xs text-slate-500">Issued: {fmtDate(doc.created_at)}</div>
          </div>
        </div>

        <div className="py-5 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Employee</div>
            <div className="font-semibold text-[#0B132B] mt-1">{doc.employee?.name}</div>
            <div className="text-slate-500">{doc.employee?.position}</div>
            {doc.employee?.id_number && <div className="text-slate-500">ID: {doc.employee.id_number}</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="bg-[#0B132B] text-white px-3 py-2 text-sm font-semibold rounded-t">Earnings</div>
            <table className="w-full text-sm border border-slate-200 border-t-0">
              <tbody>
                {doc.earnings.map((e, i) => (
                  <tr key={i} className="border-b border-slate-100"><td className="px-3 py-2 text-slate-600">{e.description}</td><td className="px-3 py-2 text-right font-medium text-[#0B132B]">{money(e.amount)}</td></tr>
                ))}
                <tr className="bg-slate-50 font-semibold"><td className="px-3 py-2 text-[#0B132B]">Gross</td><td className="px-3 py-2 text-right text-[#0B132B]">{money(doc.gross)}</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <div className="bg-[#0B132B] text-white px-3 py-2 text-sm font-semibold rounded-t">Deductions</div>
            <table className="w-full text-sm border border-slate-200 border-t-0">
              <tbody>
                <tr className="border-b border-slate-100"><td className="px-3 py-2 text-slate-600">PAYE (Tax)</td><td className="px-3 py-2 text-right text-[#0B132B]">{money(doc.paye)}</td></tr>
                <tr className="border-b border-slate-100"><td className="px-3 py-2 text-slate-600">UIF</td><td className="px-3 py-2 text-right text-[#0B132B]">{money(doc.uif)}</td></tr>
                {doc.deductions.map((d, i) => (
                  <tr key={i} className="border-b border-slate-100"><td className="px-3 py-2 text-slate-600">{d.description}</td><td className="px-3 py-2 text-right text-[#0B132B]">{money(d.amount)}</td></tr>
                ))}
                <tr className="bg-slate-50 font-semibold"><td className="px-3 py-2 text-[#0B132B]">Total</td><td className="px-3 py-2 text-right text-[#0B132B]">{money(doc.total_deductions)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-6 py-3 text-right">
            <div className="text-xs uppercase tracking-wider text-emerald-600 font-semibold">Net Pay</div>
            <div className="font-display text-2xl font-bold text-emerald-700" data-testid="payslip-net-pay">{money(doc.net)}</div>
          </div>
        </div>

        <div className="text-center text-[10px] text-slate-400 mt-8 pt-5 border-t border-slate-200">{s?.company_name} · This is a computer-generated payslip.</div>
      </div>
    </div>
  );
}
